import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { WelderDetailedAnalysis } from '../../types/stats';

interface WelderDefectHeatmapProps {
  data: WelderDetailedAnalysis;
  showPositions?: boolean;
}

// Helper function to sort positions in clock order (12, 1, 2, ..., 11)
const sortClockOrder = (positions: string[]): string[] => {
  // Extract numeric value from position string
  const getPositionNumber = (pos: string): number => {
    const normalized = pos.toLowerCase().trim();
    // Try to extract number
    const match = normalized.match(/(\d+)/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num >= 1 && num <= 12) {
        return num;
      }
    }
    return 99; // Unknown positions go to end
  };

  // Create a copy to avoid mutating the original array
  const sorted = [...positions].sort((a, b) => {
    const aNum = getPositionNumber(a);
    const bNum = getPositionNumber(b);
    
    // Handle 12 specially - it should come first
    if (aNum === 12 && bNum !== 12) return -1;
    if (bNum === 12 && aNum !== 12) return 1;
    if (aNum === 12 && bNum === 12) return 0;
    
    // For others (1-11), sort numerically
    // This ensures 1, 2, 3, ..., 10, 11 (not 1, 11, 2, 3...)
    return aNum - bNum;
  });

  return sorted;
};

export const WelderDefectHeatmap = ({ data, showPositions = false }: WelderDefectHeatmapProps) => {
  const option = useMemo(() => {
    const matrix = showPositions ? data.welderPositionMatrix : data.welderDefectMatrix;
    let categories = showPositions ? [...data.allPositions] : [...data.allDefectTypes];
    
    // Sort positions in clock order if showing positions
    if (showPositions) {
      categories = sortClockOrder(categories);
      // Verify the sort worked - positions should be: 12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11
    }
    
    const welders = Object.keys(matrix).sort();
    
    if (welders.length === 0 || categories.length === 0) {
      return null;
    }

    // Build heatmap data - use the sorted categories array
    const heatmapData: Array<[number, number, number]> = [];
    let maxValue = 0;
    
    // Calculate total counts for each category (defect type or position)
    const categoryTotals = new Map<string, number>();
    
    welders.forEach((welder, welderIdx) => {
      categories.forEach((category, categoryIdx) => {
        // Try to find the value - handle case-insensitive matching for positions
        let value = matrix[welder]?.[category] || 0;
        
        // If not found and showing positions, try case-insensitive match
        if (value === 0 && showPositions && matrix[welder]) {
          const welderData = matrix[welder];
          const matchingKey = Object.keys(welderData).find(
            key => key.toLowerCase().trim() === category.toLowerCase().trim()
          );
          if (matchingKey) {
            value = welderData[matchingKey];
          }
        }
        
        // Accumulate total for this category
        if (value > 0) {
          const currentTotal = categoryTotals.get(category) || 0;
          categoryTotals.set(category, currentTotal + value);
        }
        
        if (value > 0) {
          heatmapData.push([categoryIdx, welderIdx, value]);
          maxValue = Math.max(maxValue, value);
        }
      });
    });

    return {
      tooltip: {
        position: 'top',
        formatter: (params: any) => {
          const welder = welders[params.data[1]];
          const category = categories[params.data[0]];
          const value = params.data[2];
          return `${showPositions ? 'Position' : 'Defect Type'}: ${category}<br/>Welder: ${welder}<br/>Count: ${value}`;
        },
      },
      grid: {
        height: '60%',
        top: '15%',
        left: '15%',
        right: '10%',
        bottom: '10%',
      },
      xAxis: {
        type: 'category',
        data: categories,
        position: 'top',
        splitArea: {
          show: true,
        },
        axisLabel: {
          rotate: 45,
          formatter: (value: string) => {
            const total = categoryTotals.get(value) || 0;
            return `${value}\n(${total})`;
          },
          fontSize: 11,
          lineHeight: 14,
        },
      },
      yAxis: {
        type: 'category',
        data: welders.map(w => `Welder ${w}`),
        splitArea: {
          show: true,
        },
      },
      visualMap: {
        min: 0,
        max: maxValue,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        top: '2%',
        inRange: {
          color: ['#e0f2fe', '#0ea5e9', '#0284c7', '#0369a1', '#075985'],
        },
      },
      series: [
        {
          name: showPositions ? 'Position Count' : 'Defect Count',
          type: 'heatmap',
          data: heatmapData,
          label: {
            show: true,
            formatter: (params: any) => {
              return params.data[2] > 0 ? params.data[2] : '';
            },
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    } as echarts.EChartsOption;
  }, [data, showPositions]);

  if (!option) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No data available for heatmap.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="text-md font-semibold text-slate-900">
        {showPositions ? 'Welders vs Positions' : 'Welders vs Defect Types'}
      </h4>
      <ReactECharts 
        option={option} 
        style={{ height: Math.max(400, Object.keys(showPositions ? data.welderPositionMatrix : data.welderDefectMatrix).length * 30) }} 
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
};

